import { Injectable } from '@angular/core';
import type { TreeNode } from '@cdw-ae/common-ui-components-tree';
// biome-ignore lint/style/useImportType: JobService is used at runtime in constructor and effects
import { JobService } from '@cdw-ae/job-service';

import type { CdwSelectOption } from '@cdw-ae/shared-interfaces';
// biome-ignore lint/style/useImportType: Actions is used as injection token in constructor
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap, takeUntil } from 'rxjs';
import type { FieldUpdatesSummary } from '../models/bulk-update-action-models';
import * as BulkUpdateActions from './cdw-bulk-update-action.actions';

@Injectable()
export class BulkUpdateActionEffects {
  constructor(
    private readonly actions$: Actions,
    private jobService: JobService
  ) {}

  public getBulkUpdateActionJob = createEffect(() =>
    this.actions$.pipe(
      ofType(BulkUpdateActions.getBulkUpdateActionJob),
      switchMap((action) => {
        if (action.jobId) {
          return this.jobService.getById(action.customerId, action.jobId).pipe(
            takeUntil(
              this.actions$.pipe(
                ofType(
                  BulkUpdateActions.getBulkUpdateActionJob,
                  BulkUpdateActions.unsubscribeBulkUpdateActionJob
                )
              )
            ),
            map((job) =>
              BulkUpdateActions.getBulkUpdateActionSuccess({
                id: action.id,
                job,
              })
            ),
            catchError((_error: unknown) => {
              return of(
                BulkUpdateActions.getBulkUpdateActionSuccess({
                  id: action.id,
                  job: undefined,
                })
              );
            })
          );
        }
        return of(
          BulkUpdateActions.getBulkUpdateActionSuccess({
            id: action.id,
            job: undefined,
          })
        );
      })
    )
  );

  public getFieldUpdatesSummary = createEffect(() =>
    this.actions$.pipe(
      ofType(BulkUpdateActions.getFieldUpdatesSummary),
      map((action) => {
        let fieldUpdatesSummary: FieldUpdatesSummary[] = [];
        if (action.fieldUpdatesComponents) {
          fieldUpdatesSummary = action.fieldUpdatesComponents.map(
            (fieldUpdateComponent) => {
              let value = '';
              if (fieldUpdateComponent.fieldUpdateConfig.template === 'text') {
                value = fieldUpdateComponent.fieldUpdate.value as string;
              } else if (
                fieldUpdateComponent.fieldUpdateConfig.template === 'tree'
              ) {
                if (fieldUpdateComponent.fieldUpdateConfig.selectOptions) {
                  const nodeValue = this.findSelectedTreeNode(
                    fieldUpdateComponent.fieldUpdateConfig.selectOptions,
                    fieldUpdateComponent.fieldUpdate.value
                  );
                  if (nodeValue) {
                    value = nodeValue as string;
                  }
                }
              } else {
                fieldUpdateComponent.fieldUpdateConfig.selectOptions?.forEach(
                  (option: CdwSelectOption) => {
                    if (
                      option.value === fieldUpdateComponent.fieldUpdate.value
                    ) {
                      value = option.displayName;
                    }
                  }
                );
              }
              return {
                value,
                name: fieldUpdateComponent.fieldUpdate.name.displayName,
                updateType:
                  fieldUpdateComponent.fieldUpdateConfig.updateTypes.find(
                    (type) =>
                      type.value === fieldUpdateComponent.fieldUpdate.updateType
                  )?.displayName,
              };
            }
          );
        }
        return BulkUpdateActions.getFieldUpdatesSummarySuccess({
          id: action.id,
          summary: fieldUpdatesSummary,
        });
      })
    )
  );

  private findSelectedTreeNode(
    options: TreeNode[],
    targetValue: unknown
  ): unknown {
    for (const option of options) {
      if (option.value === targetValue) {
        return option.value;
      }
      if (option.children) {
        const found = this.findSelectedTreeNode(option.children, targetValue);
        if (found) {
          return found;
        }
      }
    }
    return undefined;
  }
}
